import { useMemo, useRef } from "react";
import DemandStudioMedicalBillsTable from "./DemandStudioMedicalBillsTable";
import DemandStudioSearchableTextarea from "./DemandStudioSearchableTextarea";
import DemandStudioTabBar from "./DemandStudioTabBar";
import { useDemandStudioContext } from "../../contexts/DemandStudioContext";

const documentClasses = "font-secondary text-content";

const sectionBase =
  "mb-6 border border-primary-surface border-l-4 border-l-primary-light-2 " +
  "rounded-[calc(var(--spacing)*2.5)] bg-white py-3.5 px-4 last:mb-0!";

const sectionFocused =
  "bg-primary-surface! border-4! border-primary-tint! rounded-[calc(var(--spacing)*2.5)] " +
  "py-3! px-4! overflow-visible " +
  "[&_.demand-studio-document-content]:overflow-visible " +
  "[&_.input-field]:overflow-visible";

// const headingWrapClasses =
//   "flex flex-row items-center justify-between gap-2";

// const documentSectionOrderClasses =
//   "text-[calc(var(--spacing)*2.75)] font-bold leading-4 uppercase tracking-[calc(var(--spacing)*0.225)] text-subtle";

// const documentHeadingBase =
//   "text-xl font-bold leading-7 text-content m-0! mb-2.5!";
// const documentHeadingFocused = "mb-2!";

// const headingClassName = `${documentHeadingBase} ${isFocused ? documentHeadingFocused : ""}`;

const documentContentClasses =
  "demand-studio-document-content text-sm leading-[calc(var(--spacing)*5.25)] text-content";

const documentTextareaClasses =
  "w-full font-secondary text-sm leading-[calc(var(--spacing)*5.25)] text-content " +
  "border-0 bg-transparent p-0 resize-none whitespace-pre-wrap " +
  "overflow-y-auto! focus:outline-none";


const DemandStudioDocumentView = () => {
  const {
    sectionDefinitions,
    sectionTexts,
    applicableSectionIds,
    updateSectionText,
    medicalBillRecordsByClientId,
    updateMedicalBillRecordField,
    addMedicalBillRecordRow,
    removeMedicalBillRecordRow,
    focusedSectionId,
    handleSectionFocus,
    handleSectionBlur,
    sectionRefs,
    isTabView,
    activeTabType,
    tabs,
    handleTabClick,
    activeMatch,
    hasSearch,
  } = useDemandStudioContext();

  const medicalTreatmentSections = sectionDefinitions.filter(
    (section) => section.type === "medicalTreatmentByClient"
  );
  const isMultiPlaintiff = medicalTreatmentSections.length > 1;

  const getMedicalBillsTitle = (section) => {
    if (!isMultiPlaintiff) {
      return "Medical Bills";
    }
    const clientName = String(section?.label || "")
      .replace(/\s*medical treatment$/i, "")
      .trim();
    return clientName ? `${clientName} Medical Bills` : "Medical Bills";
  };

  const resolvedFocusedSectionId = hasSearch ? null : focusedSectionId;

  // remember sections that ever held content so clearing a field while editing does not
  // unmount its textarea and leave the user unable to type it back in
  const sectionsWithContentRef = useRef(new Set());
  Object.entries(sectionTexts).forEach(([sectionId, text]) => {
    if (text?.trim()) sectionsWithContentRef.current.add(sectionId);
  });

  const visibleSections = useMemo(() => {
    const base = isTabView
      ? sectionDefinitions.filter((s) => s.type === activeTabType)
      : sectionDefinitions;
    // applicableSectionIds comes from persisted snapshots, so it survives the reload the ref cannot
    return base.filter(
      (s) =>
        applicableSectionIds?.has(s.id) ||
        sectionsWithContentRef.current.has(s.id) ||
        sectionTexts[s.id]?.trim()
    );
  }, [isTabView, activeTabType, sectionDefinitions, sectionTexts, applicableSectionIds]);

  const visibleMedicalBillSections = useMemo(() => {
    if (!isTabView) return medicalTreatmentSections;
    if (activeTabType !== "medicalTreatmentByClient") return [];
    return medicalTreatmentSections;
  }, [isTabView, activeTabType, medicalTreatmentSections]);

  return (
    <div className={documentClasses}>
      {isTabView && (
        <DemandStudioTabBar
          tabs={tabs}
          activeTabType={activeTabType}
          onTabClick={handleTabClick}
        />
      )}
      {visibleMedicalBillSections.map((section) => (
        <DemandStudioMedicalBillsTable
          key={`medical-bills-${section.id}`}
          clientId={section.targetId}
          records={medicalBillRecordsByClientId?.[section.targetId] || []}
          onFieldChange={updateMedicalBillRecordField}
          onAddRow={addMedicalBillRecordRow}
          onRemoveRow={removeMedicalBillRecordRow}
          title={getMedicalBillsTitle(section)}
        />
      ))}
      {visibleSections.map((section) => {
        const text = sectionTexts[section.id] || "";
        const isFocused = resolvedFocusedSectionId === section.id;
        const activeMatchStartIndex =
          activeMatch?.sectionId === section.id ? activeMatch?.matchStartIndex : -1;
        const sectionClassName = [
          sectionBase,
          isFocused ? sectionFocused : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <section
            key={section.id}
            ref={(el) => {
              if (sectionRefs) sectionRefs.current[section.id] = el;
            }}
            className={sectionClassName}
            data-section-id={section.id}
          >
            <div className={documentContentClasses}>
              <DemandStudioSearchableTextarea
                value={text}
                onChange={(e) =>
                  updateSectionText({
                    sectionId: section.id,
                    value: e.target.value,
                    section,
                  })
                }
                onFocus={() => handleSectionFocus?.(section.id)}
                onBlur={handleSectionBlur}
                placeholder="No content available for this section"
                className={documentTextareaClasses}
                minHeight={isTabView ? "12rem" : "4rem"}
                activeMatchStartIndex={activeMatchStartIndex}
              />
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default DemandStudioDocumentView;
